/**
 * useABTest Hook
 *
 * Custom hook for accessing A/B test variants in React components.
 * Provides a simple interface to get variant assignments and loading state.
 *
 * Features:
 * - Automatic variant fetching on mount
 * - Loading state management
 * - Error handling with graceful degradation
 * - Returns 'control' variant on error
 *
 * Usage:
 * ```jsx
 * import { useABTest } from '../hooks/useABTest';
 *
 * function SearchPage() {
 *   const { variant, isLoading } = useABTest('search_layout_v2');
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return variant === 'variant' ? <NewLayout /> : <OldLayout />;
 * }
 * ```
 *
 * Part of: MVP 1.10 - A/B Testing & Experimentation Framework
 * Location: apps/web/src/hooks/useABTest.js
 * Created: 2025-10-31
 */

import { useState, useEffect } from 'react';
import { useABTestContext } from '../contexts/ABTestContext';

/**
 * Hook to get variant for a specific experiment
 *
 * @param {string} experimentName - Name of the experiment
 * @returns {Object} - { variant, isLoading, error }
 */
export function useABTest(experimentName) {
  const context = useABTestContext();
  const [variant, setVariant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchVariant() {
      try {
        setIsLoading(true);
        const assignedVariant = await context.getVariant(experimentName);

        if (isMounted) {
          // Default to 'control' if no variant assigned (graceful degradation)
          setVariant(assignedVariant || 'control');
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          console.error(`[useABTest] Error fetching variant for ${experimentName}:`, err);
          setError(err.message);
          setVariant('control'); // Default to control on error
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchVariant();

    return () => {
      isMounted = false;
    };
  }, [experimentName, context]);

  return {
    variant,
    isLoading,
    error,
    // Convenience booleans
    isControl: variant === 'control',
    isVariant: variant === 'variant',
    // Access to tracking methods
    trackClick: (properties) => context.trackClick(experimentName, properties),
    trackConversion: (properties) => context.trackConversion(experimentName, properties),
    trackFilterChange: (properties) => context.trackFilterChange(experimentName, properties)
  };
}

/**
 * Hook to get all active experiments
 *
 * Useful for dashboards or admin panels that need to display all experiments.
 *
 * @returns {Object} - { experiments, isLoading, error }
 */
export function useAllABTests() {
  const context = useABTestContext();

  return {
    experiments: context.experiments,
    isLoading: context.loading,
    error: context.error
  };
}

export default useABTest;
